import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Timer, Megaphone, ExternalLink, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const formatTimeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
};

const STATUS_META = {
  success: { color: 'border-green-300 bg-green-50', icon: CheckCircle, iconColor: 'text-green-700', label: 'Generated' },
  publish_failed: { color: 'border-orange-300 bg-orange-50', icon: AlertCircle, iconColor: 'text-orange-700', label: 'Publish failed' },
  failed: { color: 'border-red-300 bg-red-50', icon: AlertCircle, iconColor: 'text-red-700', label: 'Failed' },
  skipped: { color: 'border-slate-200 bg-slate-50', icon: Timer, iconColor: 'text-slate-400', label: 'Skipped' },
};

const AutoPostHistory = ({ shopId, compact = false }) => {
  const { api: authApi } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await authApi.get(`/shops/${shopId}/social/auto-post-history`);
      setEntries(res.data.entries || []);
    } catch (e) { setEntries([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (shopId) fetch(); }, [shopId]);

  if (loading) {
    return (
      <div className="p-4 text-center text-xs text-slate-400" data-testid="auto-post-history-loading">
        <RefreshCw className="w-4 h-4 inline animate-spin" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="p-4 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-sm" data-testid="auto-post-history-empty">
        No auto-post history yet. New entries appear when stock is intaked while automation is on.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="auto-post-history">
      {entries.map((e, idx) => {
        const meta = STATUS_META[e.status] || STATUS_META.skipped;
        const Icon = meta.icon;
        const platformOk = Object.entries(e.platform_results || {})
          .filter(([, r]) => r?.ok)
          .map(([p]) => p);
        return (
          <div key={e.id || idx}
            className={`flex items-start gap-3 p-3 border rounded-sm ${meta.color}`}
            data-testid={`history-entry-${idx}`}>
            {/* Thumbnail or icon */}
            {e.status === 'success' && e.post_id ? (
              <img
                src={`${API}/api/shops/${shopId}/social/posts/${e.post_id}/image`}
                alt=""
                className="w-12 h-12 object-cover rounded-sm flex-shrink-0 bg-slate-100"
                onError={(ev) => { ev.target.style.display = 'none'; }}
              />
            ) : (
              <div className={`w-12 h-12 flex items-center justify-center rounded-sm flex-shrink-0 bg-white border ${meta.iconColor.replace('text-', 'border-')}`}>
                <Megaphone className={`w-5 h-5 ${meta.iconColor}`} />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Icon className={`w-4 h-4 ${meta.iconColor} flex-shrink-0`} />
                <span className="text-sm font-medium text-[#1e3a5f]">{meta.label}</span>
                {e.product_count > 0 && (
                  <span className="text-xs text-slate-500">· {e.product_count} product{e.product_count > 1 ? 's' : ''}</span>
                )}
                {e.mode && (
                  <span className="text-xs text-slate-400 capitalize">· {e.mode.replace(/_/g, ' ')}</span>
                )}
                <span className="text-xs text-slate-400">· {formatTimeAgo(e.at)}</span>
              </div>

              {e.status === 'success' && platformOk.length > 0 && (
                <p className="text-xs text-green-700 mt-0.5">Published to: {platformOk.join(', ')}</p>
              )}
              {e.status === 'failed' && e.error && (
                <p className="text-xs text-red-700 mt-0.5 truncate" title={e.error}>{e.error}</p>
              )}
              {e.status === 'publish_failed' && (
                <p className="text-xs text-orange-700 mt-0.5">
                  {Object.entries(e.platform_results || {}).map(([p, r]) =>
                    r?.ok ? null : <span key={p} className="block truncate">• <strong className="capitalize">{p}</strong>: {r?.error}</span>
                  )}
                </p>
              )}
              {e.status === 'skipped' && e.reason && (
                <p className="text-xs text-slate-500 mt-0.5 truncate" title={e.reason}>{e.reason}</p>
              )}
            </div>

            {e.post_id && (
              <a href="/shop/social"
                className="text-xs text-[#22c55e] hover:underline flex items-center gap-1 flex-shrink-0"
                data-testid={`history-link-${idx}`}>
                View <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        );
      })}
      {!compact && entries.length > 0 && (
        <p className="text-xs text-slate-400 pt-1">Showing last {entries.length} attempts · entries older than 90 days are auto-deleted</p>
      )}
    </div>
  );
};

export default AutoPostHistory;
